import { ANALYTICS_EVENTS } from '@syncanix/shared';

import { track } from '@/analytics/track';
import { Accented, Band, CtaArrow, ctaClasses, Lead, SectionTitle } from '@/components/bedrock';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { TRUST_BADGES } from '@/features/footer';

import type { ReactElement } from 'react';

/**
 * The closing CTA.
 *
 * It restates the hero's promise in the hero's own words — the same gradient
 * phrase, one size down — because a visitor who has scrolled the whole page
 * has been shown six demos since reading it, and the ask should land against
 * the promise rather than against the last demo.
 *
 * The offer is the design-partner one, so the primary action is "talk to us"
 * rather than a self-serve sign-up: no payment processor is connected and the
 * chrome must never advertise a trial that does not exist.
 *
 * ## Why it is dark
 *
 * It was a plain white band: the last thing a visitor sees on the page, set on
 * the same ground as the four sections before it, with no device on it at all.
 * The page's rhythm already alternates light prose against dark demos, and
 * ending on the light half meant the argument trailed off rather than closing.
 * A dark inset band with the brand wash gives it the weight of the demos it is
 * asking about, and it is the one place after the hero where the ramp is
 * painted large.
 *
 * The gradient phrase switches ramps with the ground — `onDark` is not
 * cosmetic here. The light ramp measures 2.86:1 on this band.
 *
 * The trust badges ride along the bottom edge because this is the moment the
 * ask is made, and the three facts a buyer needs in order to say yes were
 * otherwise 800px back up the page in the objections band.
 */
export function ClosingCta(): ReactElement {
  const { t } = useT();

  return (
    <Band
      tone="band"
      space="lg"
      ambient
      outerClassName="pb-[104px] pt-4"
      className="text-center"
      aria-labelledby="closing-title"
    >
      <div data-bd-rise>
        <SectionTitle
          id="closing-title"
          onDark
          className="!text-[clamp(1.9rem,4.2vw,3.4rem)] !leading-[1.02] !tracking-[-0.042em]"
        >
          <Accented onDark text={t('home.closing.title')} accent={t('home.closing.titleAccent')} />
        </SectionTitle>

        <Lead onDark size="lg" className="mx-auto mt-5 max-w-[52ch]">
          {t('home.closing.lead')}
        </Lead>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <LocalizedLink
            to="/contact"
            className={ctaClasses({ onDark: true })}
            onClick={() => {
              track(ANALYTICS_EVENTS.MARKETING_CONTACT_CTA_CLICKED, { location: 'home-closing' });
            }}
          >
            {t('home.closing.ctaPrimary')}
            <CtaArrow />
          </LocalizedLink>
          <LocalizedLink
            to="/pricing"
            className={ctaClasses({ variant: 'secondary', onDark: true })}
          >
            {t('home.closing.ctaSecondary')}
          </LocalizedLink>
        </div>

        {/* The same `TRUST_BADGES` source both footers and the objections band
            render — this is the section most likely to attract an overclaim, so
            nothing in it is hand-written here. */}
        <ul
          aria-label={t('footer.trustBadges')}
          className="mt-12 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 border-t border-[color:var(--bd-band-line)] pt-8"
        >
          {TRUST_BADGES.map((badge) => (
            <li
              key={badge.labelKey}
              className="flex items-center gap-2.5 text-[0.83rem] text-[color:var(--bd-band-dim)]"
            >
              <span
                aria-hidden="true"
                className="size-1.5 flex-none rounded-full"
                style={{
                  // In-progress claims are amber, not green — the same rule the
                  // objections band states, for the same reason: on this page a
                  // compliance state is the thing most likely to be over-read.
                  background: badge.tone === 'in-progress' ? 'var(--bd-warn)' : 'var(--bd-ok)',
                }}
              />
              {t(badge.labelKey)}
            </li>
          ))}
        </ul>
      </div>
    </Band>
  );
}
